import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { InnerBlock, GridCards } from "@/components/InnerBlock";
import { InnerCTA } from "@/components/InnerCTA";

const STEPS = [
  { n: "01", title: "Discover", meta: "Free, 30 minutes to one week",
    body: "A call with an engineer. We ask about the business problem, not the feature list. You get a one-page brief: what we understood, the risks we see, the recommended approach and a cost band. It is yours to keep, including to take elsewhere." },
  { n: "02", title: "Scope", meta: "One to two weeks",
    body: "The work broken into modules, each priced. You see what the driver app costs against the admin dashboard, and you can cut, defer or stretch any of it.",
    extras: ["Fixed price for defined scope", "Time and materials capped by sprint", "Dedicated team for three months or more"] },
  { n: "03", title: "Design", meta: "Two to three weeks",
    body: "Wireframes, then high-fidelity screens in English and Arabic. You click a working prototype before code is written. Changing a screen costs an hour; changing a built feature costs a week. Be difficult with us here." },
  { n: "04", title: "Build", meta: "Two-week sprints",
    body: "Working software at the end of every sprint, not a status report. Written sprint notes. Change requests priced before they are built. Stop or change direction at any sprint boundary." },
  { n: "05", title: "Test", meta: "Continuous, plus a release gate",
    body: "Functional, performance, security and real-device testing. Nothing reaches production without a signed test pass." },
  { n: "06", title: "Launch", meta: "Go-live",
    body: "Deployment, monitoring, error tracking and a rollback plan, with us on standby for the first seventy-two hours. You receive source code, documentation, architecture diagrams, runbooks and credentials. It is your software." },
  { n: "07", title: "Operate", meta: "Support plans",
    body: "Choose a plan or take a clean handover with a 90-day warranty and two weeks of knowledge transfer.",
    plans: [
      { name: "Essential", sla: "Next business day", inc: "Bug fixes, security patches, monitoring, backups" },
      { name: "Business", sla: "4 business hours", inc: "Adds minor enhancements and monthly reporting" },
      { name: "Critical", sla: "1 hour, 24/7", inc: "Adds on-call, quarterly review, priority releases" },
    ] },
];

export default function HowWeWorkPage() {
  return (
    <div data-testid="how-we-work-page">
      <Seo title="How We Work — Our Development Process" description="Discovery, fixed scope, two-week sprints and a written SLA. How a project with Wafa Solutions actually runs." />
      <PageHero
        eyebrow="Company"
        h1="How we work"
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Company", to: "/company" }, { label: "How we work" }]}
      />

      <InnerBlock tint="white" heading="Most projects fail quietly, then all at once.">
        <div className="max-w-3xl space-y-4 text-base leading-relaxed text-wafa-ink/85 lg:text-lg">
          <p>The pattern rarely changes. The scope was agreed verbally. Nothing was visible for four months. By the time anyone saw working software, the budget was spent and the requirement had moved.</p>
          <p>Every step below exists because we watched that happen somewhere and decided to design it out.</p>
        </div>
      </InnerBlock>

      <InnerBlock tint="cream" heading="Seven steps, from first call to years-later support">
        <ol className="space-y-8">
          {STEPS.map((s) => (
            <li key={s.n} data-testid={`how-step-${s.n}`} className="rounded-lg border border-wafa-deep/10 bg-white p-6 lg:p-8">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <div className="font-heading text-3xl font-semibold text-wafa-primary">{s.n}</div>
                <div className="font-heading text-xl font-semibold text-wafa-deep sm:text-2xl">{s.title}</div>
                <div className="text-xs uppercase tracking-widest text-wafa-ink/60">{s.meta}</div>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-wafa-ink/85 lg:text-base">{s.body}</p>
              {s.extras ? (
                <ul className="mt-4 grid gap-1.5 sm:grid-cols-3">
                  {s.extras.map((e) => (
                    <li key={e} className="rounded-md border border-wafa-deep/10 bg-wafa-cream/40 px-3 py-2 text-xs font-medium text-wafa-deep">
                      {e}
                    </li>
                  ))}
                </ul>
              ) : null}
              {s.plans ? (
                <div className="mt-5 overflow-x-auto rounded-lg border border-wafa-deep/10">
                  <table className="w-full min-w-[560px] text-sm">
                    <thead className="bg-wafa-cream/40 text-start text-xs uppercase tracking-wider text-wafa-deep">
                      <tr><th className="px-4 py-2">Plan</th><th className="px-4 py-2">SLA</th><th className="px-4 py-2">Includes</th></tr>
                    </thead>
                    <tbody className="divide-y divide-wafa-deep/10">
                      {s.plans.map((p) => (
                        <tr key={p.name}>
                          <td className="px-4 py-3 font-semibold text-wafa-deep">{p.name}</td>
                          <td className="px-4 py-3 text-wafa-ink/80">{p.sla}</td>
                          <td className="px-4 py-3 text-wafa-ink/80">{p.inc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </li>
          ))}
        </ol>
      </InnerBlock>

      <InnerBlock tint="white" heading="What we need from you">
        <p className="max-w-3xl text-base leading-relaxed text-wafa-ink/85 lg:text-lg">
          Honest answers about the business. One decision-maker who can approve. Feedback within three working days. And access to whoever actually knows how the current process works — that last one matters more than everything else combined.
        </p>
      </InnerBlock>

      <InnerCTA heading="Ready to start with a scoping call?" />
    </div>
  );
}
